import { RootSchema } from "../../redux/reducers";
import { isTrialPlan } from "../../utils";
import { getAnalyticsState } from "./stateAccess";
import { AnalyticsEvent, AnalyticsProperties, TenantType } from "./types";

const DEFAULT_SOURCE = "admin_console";
const MAX_STRING_LENGTH = 300;
const SENSITIVE_KEY_PATTERN = /(secret|token|certificate|password|key|private|authorization)/i;

const getPathname = () => {
  if (typeof window === "undefined") {
    return "";
  }

  return window.location.pathname || "";
};

const getScreen = (pathname: string) => {
  const segments = pathname.split("/").filter(Boolean);
  return segments.join("_") || "root";
};

const cleanString = (value: string) => value.trim().slice(0, MAX_STRING_LENGTH);

const sanitizeValue = (key: string, value: unknown, forError = false): unknown => {
  if (value === undefined || value === null || typeof value === "function") {
    return undefined;
  }

  if (forError && SENSITIVE_KEY_PATTERN.test(key)) {
    return undefined;
  }

  if (typeof value === "string") {
    return cleanString(value);
  }

  if (typeof value === "number" || typeof value === "boolean") {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeValue(key, item, forError))
      .filter((item) => item !== undefined)
      .slice(0, 20);
  }

  if (!forError && typeof value === "object") {
    return value;
  }

  return undefined;
};

type AppInfoLike = {
  AppId?: string | number;
  AppName?: string;
  isSuccess?: boolean;
  ProductPlan?: { Name?: string };
  IsB2BTenant?: boolean;
};

/**
 * Resolves tenantType from app info or feature flags.
 * Prefer IsB2BTenant on the site payload; fall back to enable_b2b_identity.
 */
export const resolveTenantType = (state?: RootSchema, currentAppInfo?: AppInfoLike): TenantType | undefined => {
  const appInfo = currentAppInfo || state?.auth?.currentAppinfo;
  if (appInfo && typeof (appInfo as AppInfoLike).IsB2BTenant === "boolean") {
    return (appInfo as AppInfoLike).IsB2BTenant ? "b2b" : "b2c";
  }

  const features = state?.auth?.appFeatures as { isSuccess?: boolean; enable_b2b_identity?: boolean } | undefined;
  if (features?.isSuccess && typeof features.enable_b2b_identity === "boolean") {
    return features.enable_b2b_identity ? "b2b" : "b2c";
  }

  return undefined;
};

/**
 * Resolves trial_user when app info is available.
 * Returns boolean only when ProductPlan context can be evaluated (isSuccess + AppName).
 */
export const resolveTrialUser = (currentAppInfo?: AppInfoLike): boolean | undefined => {
  if (!currentAppInfo?.isSuccess || !currentAppInfo?.AppName) {
    return undefined;
  }
  return isTrialPlan(currentAppInfo);
};

const buildBaseProps = (properties: AnalyticsProperties, state?: RootSchema): AnalyticsProperties => {
  const pathname = typeof properties.pathname === "string" ? properties.pathname : getPathname();
  const currentAppInfo = (properties.currentAppInfo as AppInfoLike | undefined) || state?.auth?.currentAppinfo;
  const localStorageAppName = typeof window !== "undefined" ? localStorage.getItem("siteName") || undefined : undefined;

  const base: AnalyticsProperties = {
    pathname,
    screen: typeof properties.screen === "string" ? properties.screen : getScreen(pathname),
    app_id: (properties.app_id as string | number | undefined) || currentAppInfo?.AppId,
    app_name: (properties.app_name as string | undefined) || currentAppInfo?.AppName || localStorageAppName,
    source: typeof properties.source === "string" ? properties.source : DEFAULT_SOURCE
  };

  // Always attach trial_user / tenantType when resolvable so Amplitude event
  // property breakdowns never show (none) for logged-in tenant context.
  // Explicit event properties still win via the merge below.
  const trialUser = typeof properties.trial_user === "boolean" ? properties.trial_user : resolveTrialUser(currentAppInfo);
  if (typeof trialUser === "boolean") {
    base.trial_user = trialUser;
  }

  const tenantType = properties.tenantType === "b2b" || properties.tenantType === "b2c" ? (properties.tenantType as TenantType) : resolveTenantType(state, currentAppInfo);
  if (tenantType) {
    base.tenantType = tenantType;
  }

  return base;
};

export const buildAnalyticsEvent = (event: AnalyticsEvent, state?: RootSchema): AnalyticsEvent => {
  const resolvedState = state || getAnalyticsState();
  const properties = event.properties || {};
  const { currentAppInfo: _currentAppInfo, ...rest } = properties;
  const payload: AnalyticsProperties = { ...buildBaseProps(properties, resolvedState) };

  Object.entries(rest).forEach(([key, value]) => {
    // Skip keys already finalized in base when they were resolved from state
    // only if rest value is undefined; otherwise allow explicit overrides.
    const sanitized = sanitizeValue(key, value, event.forError);
    if (sanitized !== undefined) {
      payload[key] = sanitized;
    }
  });

  return {
    ...event,
    properties: payload
  };
};

export const sanitizeAnalyticsProperties = (properties: AnalyticsProperties, forError = false): AnalyticsProperties => {
  const payload: AnalyticsProperties = {};

  Object.entries(properties).forEach(([key, value]) => {
    const sanitized = sanitizeValue(key, value, forError);
    if (sanitized !== undefined) {
      payload[key] = sanitized;
    }
  });

  return payload;
};
